import { create } from 'zustand'
import {
  integrateTime,
  shouldSoftStopAtEndpoint,
} from '../core/time/timeEngine'
import type { QualityTier } from '../config/quality'
import { INITIAL_TIME_VALUE } from '../config/eras'

export interface ExperienceState {
  timeValue: number
  timeSpeed: number
  traverseIntensity: number
  pausedByUser: boolean
  speedBeforePause: number

  inputMode: 'gesture' | 'mouse' | 'keyboard'
  isUiDragging: boolean
  cameraGranted: boolean | null
  handsVisible: boolean
  palmNdc: { x: number; y: number } | null
  gestureModelStatus: 'idle' | 'loading' | 'ready' | 'failed'

  quality: QualityTier
  qualityLocked: boolean
  isReady: boolean
  loadProgress: number
  showSkeleton: boolean
  showPersonPreview: boolean
  privacyAccepted: boolean
  onboardingStep: number | null
  segmentationEnabled: boolean
  showDebug: boolean

  loadedEras: Record<string, boolean>
  missingEraToast: string | null
  bannerMessage: string | null

  setGestureControls: (p: {
    timeSpeed: number
    traverseIntensity: number
    palmNdc?: { x: number; y: number } | null
  }) => void
  setTimeAbsolute: (timeValue: number) => void
  setTimeSpeed: (timeSpeed: number) => void
  togglePauseByUser: () => void
  setUiDragging: (v: boolean) => void
  tick: (dt: number) => void
  setQuality: (q: QualityTier) => void
  setQualityLocked: (v: boolean) => void
  setCameraGranted: (v: boolean) => void
  setHandsVisible: (v: boolean) => void
  setGestureModelStatus: (s: ExperienceState['gestureModelStatus']) => void
  setLoadProgress: (p: number) => void
  setReady: (v: boolean) => void
  setSegmentationEnabled: (v: boolean) => void
  markEraLoaded: (id: string) => void
  setPrivacyAccepted: (v: boolean) => void
  setOnboardingStep: (s: number | null) => void
  setShowSkeleton: (v: boolean) => void
  setShowPersonPreview: (v: boolean) => void
  setInputMode: (m: ExperienceState['inputMode']) => void
  setBannerMessage: (m: string | null) => void
  setShowDebug: (v: boolean) => void
}

export const useExperienceStore = create<ExperienceState>((set, get) => ({
  timeValue: INITIAL_TIME_VALUE,
  timeSpeed: 0,
  traverseIntensity: 0,
  pausedByUser: false,
  speedBeforePause: 0,

  inputMode: 'mouse',
  isUiDragging: false,
  cameraGranted: null,
  handsVisible: false,
  palmNdc: null,
  gestureModelStatus: 'idle',

  quality: 'high',
  qualityLocked: false,
  isReady: false,
  loadProgress: 0,
  showSkeleton: true,
  showPersonPreview: true,
  privacyAccepted: false,
  onboardingStep: 0,
  segmentationEnabled: false,
  showDebug: false,

  loadedEras: {},
  missingEraToast: null,
  bannerMessage: null,

  setGestureControls: ({ timeSpeed, traverseIntensity, palmNdc }) => {
    const state = get()
    if (state.isUiDragging) return
    set({
      timeSpeed,
      traverseIntensity,
      palmNdc: palmNdc === undefined ? state.palmNdc : palmNdc,
      pausedByUser: false,
      inputMode: 'gesture',
      handsVisible: true,
    })
  },

  setTimeAbsolute: (timeValue) => {
    const v = Math.min(1, Math.max(0, timeValue))
    set({ timeValue: v })
  },

  setTimeSpeed: (timeSpeed) => {
    set({
      timeSpeed,
      pausedByUser: false,
    })
  },

  togglePauseByUser: () => {
    const { pausedByUser, timeSpeed, speedBeforePause } = get()
    if (!pausedByUser) {
      set({
        pausedByUser: true,
        speedBeforePause: timeSpeed,
        timeSpeed: 0,
      })
    } else {
      set({
        pausedByUser: false,
        timeSpeed: speedBeforePause,
        speedBeforePause: 0,
      })
    }
  },

  setUiDragging: (v) => {
    if (v) {
      set({ isUiDragging: true, timeSpeed: 0 })
    } else {
      set({ isUiDragging: false })
    }
  },

  tick: (dt) => {
    const state = get()
    if (state.isUiDragging) return
    let speed = state.pausedByUser ? 0 : state.timeSpeed
    const next = integrateTime(state.timeValue, speed, dt)
    if (shouldSoftStopAtEndpoint(next, speed)) {
      speed = 0
    }
    if (next !== state.timeValue || speed !== state.timeSpeed) {
      set({ timeValue: next, timeSpeed: speed })
    }
  },

  setQuality: (q) => set({ quality: q }),
  setQualityLocked: (v) => set({ qualityLocked: v }),
  setCameraGranted: (v) => set({ cameraGranted: v }),
  setHandsVisible: (v) => set({ handsVisible: v }),
  setGestureModelStatus: (s) => set({ gestureModelStatus: s }),
  setLoadProgress: (p) => set({ loadProgress: p }),
  setReady: (v) => set({ isReady: v }),
  setSegmentationEnabled: (v) => set({ segmentationEnabled: v }),
  markEraLoaded: (id) =>
    set((s) => ({ loadedEras: { ...s.loadedEras, [id]: true } })),
  setPrivacyAccepted: (v) => set({ privacyAccepted: v }),
  setOnboardingStep: (s) => set({ onboardingStep: s }),
  setShowSkeleton: (v) => set({ showSkeleton: v }),
  setShowPersonPreview: (v) => set({ showPersonPreview: v }),
  setInputMode: (m) => set({ inputMode: m }),
  setBannerMessage: (m) => set({ bannerMessage: m }),
  setShowDebug: (v) => set({ showDebug: v }),
}))
